// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineContainer, { CartLineContainerStyles } from "@synd/App/Components/Cart/CartLineContainer";
import ProductCases from "@synd/App/Components/Cart/ProductCases";
import { CartMessages } from "@synd/App/Components/MiniCart/MiniCart.helpers";
import Notify from "@synd/App/Components/Notify";
import ProductPartNumbers from "@synd/Overrides/Components/ProductPartNumbers";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    extendedStyles?: CartLineCardCondensedStyles;
    showVmiBinDetails?: boolean;
    // XNG-Changes
    showAddToList?: boolean;
    hideAddToList?: boolean;
    errorMessages?: CartMessages;
}

// XNG-Changes
const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

// XNG-Changes
const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productErpNumberText?: TypographyProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    productAvailability?: ProductAvailabilityStyles;
    cartLineErrorMessageText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    productBinNumberGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    gridContainer?: CartLineContainerStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    totalWrapper?: InjectableCss;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    addToListLink?: LinkPresentationProps;
    cartLineErrorMessageWrapper?: InjectableCss;
    notifyStyles?: TypographyProps;
}

export const cartLineCardCondensedStyles: CartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    productBrandAndDescriptionGridItem: {
        width: [12, 12, 12, 5, 4],
        css: css`
            flex-direction: column;
        `,
    },
    productErpNumberText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    productPriceAndQuantityGridItem: { width: [12, 12, 12, 4, 5] },
    productPriceAndAvailabilityGridItem: {
        width: 7,
        css: css`
            flex-direction: column;
        `,
    },
    cartLineErrorMessageText: { color: "danger" },
    quantityGridItem: { width: 5 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    productBinNumberGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
};

const CartLineCardCondensed: FC<Props> = ({
    cart,
    cartLine,
    showInventoryAvailability,
    removeCartLine,
    updateCartLine,
    extendedStyles,
    showRemoveAction,
    showVmiBinDetails,
    // XNG-Changes
    enableVat,
    vatPriceDisplay,
    showPricing,
    showAddToList = true,
    setAddToListModalIsOpen,
    addToWishList,
    wishListSettings,
    hideAddToList,
    errorMessages,
}) => {
    // XNG-Changes
    const toasterContext = useContext(ToasterContext);

    const currentMode = getCookie(NavigationModeCookieName);
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    // Xng-Changes
    // const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
    //     return current.productId === cartLine.productId
    //         ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
    //         : sum;
    // }, 0);
    // const errorMessages: React.ReactNode[] = [];
    // if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
    //     const tooManyRequestedMessage = siteMessage(
    //         "Cart_ToManyQtyRequested",
    //         cartLine.qtyOnHand.toLocaleString(),
    //         sumQtyPerUom.toLocaleString(),
    //     );
    //     errorMessages.push(tooManyRequestedMessage);
    // }

    // if (cartLine.isRestricted) {
    //     errorMessages.push(translate("Restricted product"));
    // }

    // if (!cartLine.isActive) {
    //     errorMessages.push(translate("Inactive Product"));
    // }

    // if (cartLine.cantBuy) {
    //     errorMessages.push(translate("View-only product"));
    // }

    const [styles] = React.useState(() => mergeToNew(cartLineCardCondensedStyles, extendedStyles));

    // XNG-Changes
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };
    // XNG-Changes: add isPrebook
    const isPrebook = (cartLine as any)?.isPrebook;
    // XNG-Changes: restructure and adding error messages
    return (
        <CartLineContainer
            extendedStyles={styles.gridContainer}
            isCondensed={true}
            data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <CartLineContainer.Error>
                {!!errorMessages?.length && (
                    <StyledWrapper {...styles.cartLineErrorMessageWrapper}>
                        {errorMessages.map((errorMessage, index) => (
                            <Notify
                                key={index}
                                type={errorMessage.type}
                                className="light-mode"
                                {...styles.notifyStyles}
                            >
                                {errorMessage.message}
                            </Notify>
                        ))}
                    </StyledWrapper>
                )}
                {/* XNG-Changes */}
                {parseInt(cartLine.properties.purchaseMoreDiscountQty || "0", 10) > 0 && (
                    <Notify type={"info"} className="light-mode" {...styles.notifyStyles}>
                        <Typography>{translate("Discount Availaable")}: </Typography>{" "}
                        {translate("Pruchase {0} more to recivce a discount.").replace(
                            "{0}",
                            cartLine.properties.purchaseMoreDiscountQty!,
                        )}
                    </Notify>
                )}
            </CartLineContainer.Error>
            <CartLineContainer.Description {...{ isCondensed: true }}>
                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                <ProductPartNumbers
                    manufacturerItem={cartLine.manufacturerItem}
                    productNumber={cartLine.erpNumber}
                    customerProductNumber={cartLine.customerName}
                    extendedStyles={styles.productPartNumbers}
                    showProductNumber={false}
                    {...{
                        packSize: (cartLine as any).packSize,
                    }}
                    // XNG-Changes
                    showManufacturerItem={false}
                />
                {showVmiBinDetails && currentMode === "Vmi" && (
                    <ProductBinNumbers
                        locationId={cart.vmiLocationId}
                        binId={cartLine.vmiBinId}
                        isCondensed={true}
                        extendedStyles={styles.productBinNumbers}
                    />
                )}
            </CartLineContainer.Description>
            <CartLineContainer.Cases>
                <ProductCases cases={(cartLine as any).casesSkid || "-"} />
            </CartLineContainer.Cases>
            <CartLineContainer.Price>
                {!cart.cartNotPriced && (
                    <ProductPrice
                        product={cartLine}
                        currencySymbol={cart.currencySymbol}
                        showSavings={false}
                        showInvalidPriceMessage={true}
                        extendedStyles={styles.productPrice}
                        label={translate("Price/Unit")}
                    />
                )}
                {showInventoryAvailability && !cartLine.quoteRequired && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                        {...{ isPrebook }}
                    />
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    editable={true}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Total data-test-selector="total">
                <StyledWrapper {...styles.totalWrapper}>
                    {!cartLine.quoteRequired && !cart?.cartNotPriced && cartLine.pricing && showPricing && (
                        <SmallHeadingAndText
                            heading={
                                !enableVat
                                    ? translate("Subtotal")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                      ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                      : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                            }
                            extendedStyles={styles.extendedUnitNetPrice}
                        />
                    )}
                </StyledWrapper>
            </CartLineContainer.Total>
            <CartLineContainer.Remove>
                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </CartLineContainer.Remove>
            <CartLineContainer.Actions>
                {/* XNG-Changes:  Hide the "Add to List" button for Prebook products */}
                {showAddToList &&
                    !hideAddToList &&
                    cartLine.canAddToWishlist &&
                    !cartLine.isRestricted &&
                    !isPrebook && (
                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                            {translate("Add to List")}
                        </Link>
                    )}
            </CartLineContainer.Actions>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardCondensed));
